import type React from 'react';
import { useParams } from 'react-router-dom';
import { PageContainer } from '../components/PageContainer';

import { VenueCard } from '../components/VenueCard';
import { WineRow } from '../components/WineRow';
import { PublicCheckIns } from '../components/profile/PublicCheckIns';
import { trpc } from '../lib/trpc';

const formatDate = (iso: string): string => {
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
};

const AvatarDisplay = ({ avatar, userName }: { avatar: string | null; userName: string }) => {
  const initials = userName.slice(0, 2).toUpperCase();
  return avatar ? (
    <img
      src={avatar}
      alt={userName}
      className="w-24 h-24 rounded-full object-cover border-2 border-gray-200"
    />
  ) : (
    <div className="w-24 h-24 rounded-full bg-primary-100 border-2 border-gray-200 flex items-center justify-center">
      <span className="text-2xl font-semibold text-primary-600">{initials}</span>
    </div>
  );
};

interface StatItemProps {
  label: string;
  value: number;
}

const StatItem = ({ label, value }: StatItemProps) => (
  <div className="text-center">
    <div className="text-2xl font-bold text-gray-900">{value}</div>
    <div className="text-sm text-gray-500">{label}</div>
  </div>
);

const PrivateProfileStub = ({
  userName,
  avatar,
}: {
  userName: string;
  avatar: string | null;
}) => (
  <div className="flex flex-col items-center py-12">
    <AvatarDisplay avatar={avatar} userName={userName} />
    <h1 className="mt-4 text-xl font-semibold text-gray-900">{userName}</h1>
    <p className="mt-2 text-gray-500">This profile is private</p>
  </div>
);

export const PublicProfile = () => {
  const { username } = useParams<{ username: string }>();
  const { data, isLoading, error } = trpc.user.getPublicProfile.useQuery(
    { username: username! },
    { enabled: !!username }
  );

  if (isLoading) {
    return (
      <PageContainer>
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
        </div>
      </PageContainer>
    );
  }

  if (error) {
    const is404 = error.data?.code === 'NOT_FOUND';
    return (
      <PageContainer>
        <div className="flex flex-col items-center py-12">
          <h1 className="text-xl font-semibold text-gray-900">
            {is404 ? 'User not found' : 'Something went wrong'}
          </h1>
          <p className="mt-2 text-gray-500">
            {is404
              ? `No user with username "${username}" exists.`
              : 'Unable to load this profile. Please try again.'}
          </p>
        </div>
      </PageContainer>
    );
  }

  if (!data) return null;

  return (
    <PageContainer>
      <div className="px-0 md:px-4 pt-2 pb-4 max-w-2xl mx-auto">
        {!data.isPublic ? (
          <PrivateProfileStub userName={data.userName} avatar={data.avatar} />
        ) : (
          <div className="flex flex-col items-center py-8">
            <AvatarDisplay avatar={data.avatar} userName={data.userName} />

            <h1 className="mt-4 text-xl font-semibold text-gray-900">{data.userName}</h1>

            {data.name && (
              <p className="text-gray-600">{data.name}</p>
            )}

            {data.homeLocation && (
              <p className="text-sm text-gray-500">
                {[data.homeLocation.city, data.homeLocation.state].filter(Boolean).join(', ')}
              </p>
            )}

            <p className="text-sm text-gray-400 mt-1">
              Member since {formatDate(data.memberSince)}
            </p>

            {data.bio && (
              <p className="mt-4 text-gray-700 text-center max-w-md">{data.bio}</p>
            )}

            {/* Stats bar */}
            <div className="mt-6 flex gap-8 border-t border-b border-gray-200 py-4">
              <StatItem label="Reviews" value={data.stats.reviews} />
              <StatItem label="Favorites" value={data.stats.favorites} />
            </div>

            {/* Favorites section */}
            {data.favorites && (data.favorites.venues.length > 0 || data.favorites.wines.length > 0) && (
              <div className="mt-8 w-full text-left space-y-8">
                {data.favorites.venues.length > 0 && (
                  <section>
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">
                      Favorite Venues
                    </h2>
                    <ul className="divide-y divide-gray-200">
                      {data.favorites.venues.map((venue) => (
                        <VenueCard
                          key={venue._id}
                          venue={venue}
                          logoMedia={venue.logoImage ?? null}
                        />
                      ))}
                    </ul>
                  </section>
                )}

                {data.favorites.wines.length > 0 && (
                  <section>
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">
                      Wishlist
                    </h2>
                    <div className="border-t border-gray-200">
                      {data.favorites.wines.map((wine) => (
                        <WineRow
                          key={wine._id}
                          wine={wine as unknown as React.ComponentProps<typeof WineRow>['wine']}
                        />
                      ))}
                    </div>
                  </section>
                )}
              </div>
            )}

            {username && <PublicCheckIns username={username} />}
          </div>
        )}
      </div>
    </PageContainer>
  );
};
